import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import { isAiGenerationProgressKeyForUsecase } from '@/features/datasets/utils/datasetOverviewProgress';
import { inProgressStepPercent } from '@/features/datasets/utils/generationProgressStatus';

function sameId(left, right) {
    return left != null && right != null && String(left) === String(right);
}

function displayNameFor(name) {
    if (!name) return '';
    return getUserFacingDatasetName(name) || name;
}

export function progressBelongsToUsecase(key, entry, usecaseId) {
    if (!entry || !usecaseId) return false;
    if (entry.isDownload) return false;
    if (sameId(entry.usecaseId, usecaseId)) return true;
    if (
        Array.isArray(entry.affectedUsecaseIds)
        && entry.affectedUsecaseIds.some((id) => sameId(id, usecaseId))
    ) {
        return true;
    }
    return Boolean(entry.isGeneration && isAiGenerationProgressKeyForUsecase(key, usecaseId));
}

function itemFromProgress(key, entry) {
    const fileName = displayNameFor(entry.datasetName || key) || key;
    const overall = typeof entry.progress === 'number' ? entry.progress : 0;
    const progress = entry.isGeneration && !entry.isCompleted && !entry.isError
        ? inProgressStepPercent(overall)
        : Math.max(0, Math.min(100, Math.round(overall)));

    if (entry.isError) {
        return {
            id: key,
            fileName,
            state: 'failed',
            progress,
            fileSize: '',
            helperText: entry.message || 'Upload failed',
        };
    }

    const complete = Boolean(
        entry.isCompleted || entry.isWarning || (!entry.isGeneration && progress >= 100 && !entry.isError)
    );
    if (complete) {
        return {
            id: key,
            fileName,
            state: 'success',
            progress: 100,
            fileSize: 'Added to task',
            helperText: entry.isWarning ? (entry.warningMessage || entry.message || '') : '',
        };
    }

    let helperText = 'Uploading dataset';
    if (entry.isGeneration) {
        helperText = (entry.phase === 'checking_labels' || entry.generationPhase === 'checking_labels')
            ? 'Checking labels'
            : (entry.phase === 'understanding_the_task' || entry.generationPhase === 'understanding_the_task')
                ? 'Understanding the task'
                : 'Generating examples';
    } else if (entry.phase === 'processing' || entry.phase === 'label_generation') {
        helperText = 'Processing dataset';
    }

    return {
        id: key,
        fileName,
        state: 'in-progress',
        progress,
        fileSize: '',
        helperText,
    };
}

const STATE_ORDER = { 'in-progress': 0, failed: 1, success: 2 };

/**
 * Files to surface in Improve → Data Augmentation: in-flight / recent
 * progress for this task, plus datasets attached since the modal opened.
 */
export function collectImproveAugmentationItems({
    progressStatus,
    usecaseId,
    currentDatasets = [],
    baselineDatasets = [],
} = {}) {
    const itemsByDisplayName = new Map();
    const baselineDisplay = new Set(
        (baselineDatasets || []).filter(Boolean).map((name) => displayNameFor(name)),
    );

    Object.entries(progressStatus || {}).forEach(([key, entry]) => {
        if (!progressBelongsToUsecase(key, entry, usecaseId)) return;
        if (entry?.isBenchmark) return;
        const item = itemFromProgress(key, entry);
        if (!item.fileName) return;
        itemsByDisplayName.set(item.fileName, item);
    });

    (currentDatasets || []).forEach((name) => {
        if (!name) return;
        const fileName = displayNameFor(name);
        if (!fileName || baselineDisplay.has(fileName) || itemsByDisplayName.has(fileName)) {
            return;
        }
        itemsByDisplayName.set(fileName, {
            id: name,
            fileName,
            state: 'success',
            progress: 100,
            fileSize: 'Added to task',
            helperText: '',
        });
    });

    return [...itemsByDisplayName.values()].sort((left, right) => {
        const byState = (STATE_ORDER[left.state] ?? 3) - (STATE_ORDER[right.state] ?? 3);
        if (byState !== 0) return byState;
        return left.fileName.localeCompare(right.fileName);
    });
}
